/**
 * Forward-compatible views of the shared contract.
 *
 * The server grows fields faster than `@village/shared` is republished, and
 * every addition so far has been add-only and optional. Rather than guess in
 * `shared/`, the client reads those fields through these widened types and
 * treats every one of them as "may be missing": nothing here is required for
 * the UI to render. When shared catches up these aliases keep compiling.
 */
import type { Decision, Move, Villager } from '@village/shared';

/** Which Choice questions were resolved by sampling instead of taking the top option. */
export type SampledField = 'move' | 'action' | 'goal';

export type DecisionView = Omit<Decision, 'nouls'> & {
  /** present when confidence was under the gate and the option was drawn from the top 3 */
  sampled?: SampledField[];
  /** the Noul set is open — render whatever keys arrive */
  nouls?: Record<string, number>;
};

export type VillagerView = Villager & {
  /** where the villager is walking, when the server knows */
  heading?: Move;
};

export const asDecision = (d: Decision | undefined): DecisionView | undefined => d as DecisionView | undefined;

export const asVillager = (v: Villager): VillagerView => v as VillagerView;

export const wasSampled = (d: DecisionView, field: SampledField): boolean =>
  Array.isArray(d.sampled) && d.sampled.includes(field);
